import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Save, X, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { toast } from 'sonner';

export const MapEditor = ({ 
  mapImage, 
  units, 
  projectId,
  onSaveUnit, 
  onDeleteUnit,
  onUpdateUnitPosition 
}) => {
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedUnit, setDraggedUnit] = useState(null);
  const [isAddingUnit, setIsAddingUnit] = useState(false);
  const [newUnitPosition, setNewUnitPosition] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  const [formData, setFormData] = useState({
    unit_number: '',
    status: 'available',
    price: '',
    bedrooms: 0,
    bathrooms: 0,
    area: '',
    floor: '',
    description: '',
    design_image: '',
    view_image: ''
  });

  // Zoom functions
  const zoomIn = () => setScale(prev => Math.min(prev + 0.5, 4));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.5, 0.5));
  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setScale(prev => Math.max(0.5, Math.min(4, prev + delta)));
  };

  const handleMapClick = (e) => {
    if (!isAddingUnit || isDragging) return;
    
    const rect = mapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setNewUnitPosition({ x, y });
    setFormData({
      unit_number: '',
      status: 'available',
      price: '',
      bedrooms: 0,
      bathrooms: 0,
      area: '',
      floor: '',
      description: '',
      design_image: '',
      view_image: ''
    });
    setEditDialogOpen(true);
    setIsAddingUnit(false);
  };

  const handleMarkerMouseDown = (e, unit) => {
    e.stopPropagation();
    if (isAddingUnit) return;
    setIsDragging(true);
    setDraggedUnit(unit);
  };

  const handleMapMouseMove = (e) => {
    if (isDragging && draggedUnit && mapRef.current) {
      const rect = mapRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
      setDraggedUnit({ ...draggedUnit, position_x: x, position_y: y });
    } else if (isPanning && !isAddingUnit) {
      setPosition({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMapMouseUp = async () => {
    if (isDragging && draggedUnit) {
      try {
        await onUpdateUnitPosition(draggedUnit.id, draggedUnit.position_x, draggedUnit.position_y);
        toast.success('Unit position updated');
      } catch (error) {
        toast.error('Failed to update position');
      }
    }
    setIsDragging(false);
    setDraggedUnit(null);
    setIsPanning(false);
  };

  const handleContainerMouseDown = (e) => {
    if (e.target.closest('[data-marker]') || isAddingUnit) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleEditUnit = (e, unit) => {
    e.stopPropagation();
    setSelectedUnit(unit);
    setFormData({
      unit_number: unit.unit_number,
      status: unit.status,
      price: unit.price,
      bedrooms: unit.bedrooms || 0,
      bathrooms: unit.bathrooms || 0,
      area: unit.area || '',
      floor: unit.floor || '',
      description: unit.description || '',
      design_image: unit.design_image || '',
      view_image: unit.view_image || ''
    });
    setNewUnitPosition(null);
    setEditDialogOpen(true);
  };

  const handleSaveUnit = async () => {
    if (!formData.unit_number || !formData.price) {
      toast.error('Please fill in unit number and price');
      return;
    }

    try {
      const unitData = {
        ...formData,
        project_id: projectId,
        position_x: newUnitPosition ? newUnitPosition.x : selectedUnit.position_x,
        position_y: newUnitPosition ? newUnitPosition.y : selectedUnit.position_y
      };

      await onSaveUnit(selectedUnit?.id, unitData);
      toast.success(selectedUnit ? 'Unit updated' : 'Unit created');
      setEditDialogOpen(false);
      setSelectedUnit(null);
      setNewUnitPosition(null);
    } catch (error) {
      toast.error('Failed to save unit');
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (window.confirm('Are you sure you want to delete this unit?')) {
      try {
        await onDeleteUnit(unitId);
        toast.success('Unit deleted');
        setEditDialogOpen(false);
        setSelectedUnit(null);
      } catch (error) {
        toast.error('Failed to delete unit');
      }
    }
  };

  useEffect(() => {
    window.addEventListener('mouseup', handleMapMouseUp);
    return () => window.removeEventListener('mouseup', handleMapMouseUp);
  }, [isDragging, draggedUnit]);

  const displayUnits = units.map(unit => {
    if (draggedUnit && unit.id === draggedUnit.id) {
      return draggedUnit;
    }
    return unit;
  });

  return (
    <div className="space-y-4" data-testid="map-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-4 p-4 border border-white/10">
        <button
          onClick={() => setIsAddingUnit(!isAddingUnit)}
          className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] font-light transition-colors ${
            isAddingUnit 
              ? 'bg-green-500 text-black' 
              : 'bg-white text-black hover:bg-white/90'
          }`}
          data-testid="add-unit-marker-btn"
        >
          {isAddingUnit ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {isAddingUnit ? 'Cancel' : 'Add Unit'}
        </button>
        
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={zoomOut} className="p-2 border border-white/20 hover:border-white/40 text-white/60 hover:text-white">
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-white/60 text-xs font-light px-2">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} className="p-2 border border-white/20 hover:border-white/40 text-white/60 hover:text-white">
            <ZoomIn className="w-4 h-4" />
          </button>
          <button onClick={resetZoom} className="p-2 border border-white/20 hover:border-white/40 text-white/60 hover:text-white ml-2">
            <Move className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isAddingUnit && (
        <p className="text-green-400 text-xs font-light px-4 animate-pulse">
          Click on the map to place a new unit marker
        </p>
      )}

      {/* Map Container */}
      <div 
        ref={containerRef}
        className={`relative w-full overflow-hidden border bg-black ${
          isAddingUnit ? 'border-green-500 cursor-crosshair' : 'border-white/10 cursor-grab active:cursor-grabbing'
        }`}
        onWheel={handleWheel}
        onMouseDown={handleContainerMouseDown}
        onMouseMove={handleMapMouseMove}
        data-testid="map-editor-canvas"
      >
        <div
          ref={mapRef}
          className="relative origin-center"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 0.1s'
          }}
          onClick={handleMapClick}
        >
          {/* Map Image */}
          <img
            src={mapImage || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80'}
            alt="Project Map"
            className="w-full h-auto block select-none"
            draggable={false}
          />

          {/* Unit Markers */}
          <div className="absolute inset-0">
          {displayUnits.map((unit) => (
            <div
              key={unit.id}
              data-marker
              className={`absolute rounded-full cursor-move z-10
                ${unit.status === 'available' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : ''}
                ${unit.status === 'sold' ? 'bg-red-500 opacity-70' : ''}
                ${unit.status === 'pending' ? 'bg-orange-500 shadow-[0_0_8px_rgba(251,146,60,0.6)]' : ''}
                ${isDragging && draggedUnit?.id === unit.id ? 'ring-2 ring-white scale-150' : 'hover:scale-125'}
                transition-transform duration-150`}
              style={{
                left: `${unit.position_x}%`,
                top: `${unit.position_y}%`,
                width: `${Math.max(8, 12 / scale)}px`,
                height: `${Math.max(8, 12 / scale)}px`,
                transform: 'translate(-50%, -50%)',
              }}
              onMouseDown={(e) => handleMarkerMouseDown(e, unit)}
              onDoubleClick={(e) => handleEditUnit(e, unit)}
              title={`${unit.unit_number} - Double click to edit`}
              data-testid={`unit-marker-editor-${unit.id}`}
            />
          ))}
          </div>

          {/* New Unit Preview */}
          {newUnitPosition && (
            <div
              className="absolute w-4 h-4 rounded-full bg-white/50 border-2 border-dashed border-white animate-pulse z-20"
              style={{
                left: `${newUnitPosition.x}%`,
                top: `${newUnitPosition.y}%`,
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
        </div>

        {/* Adding Mode Overlay */}
        {isAddingUnit && (
          <div className="absolute inset-0 bg-green-500/5 pointer-events-none" />
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-between p-4 border border-white/10 text-xs">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-white/60 font-light">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span className="text-white/60 font-light">Pending</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 opacity-70" />
            <span className="text-white/60 font-light">Sold</span>
          </div>
        </div>
        <span className="text-white/40">Total: {units.length} units</span>
      </div>

      {/* Edit/Add Unit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="unit-editor-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              {selectedUnit ? 'Edit Unit' : 'Add New Unit'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Unit Number *</Label>
                <Input
                  value={formData.unit_number}
                  onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                  placeholder="e.g., A-101"
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                  data-testid="editor-unit-number"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none h-12" data-testid="editor-unit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/20 rounded-none">
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Price *</Label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g., $125,000"
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                  data-testid="editor-unit-price"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Floor</Label>
                <Input
                  value={formData.floor}
                  onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                  placeholder="e.g., 5th Floor"
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Bedrooms</Label>
                <Input
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 0 })}
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Bathrooms</Label>
                <Input
                  type="number"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: parseInt(e.target.value) || 0 })}
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Area</Label>
                <Input
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  placeholder="e.g., 120 sqm"
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Unit description..."
                className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
              />
            </div>

            {/* Position Info */}
            <div className="p-3 border border-white/10">
              <p className="text-white/40 text-xs font-light">
                Position: X: {(newUnitPosition?.x || selectedUnit?.position_x || 0).toFixed(1)}%, 
                Y: {(newUnitPosition?.y || selectedUnit?.position_y || 0).toFixed(1)}%
              </p>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            {selectedUnit && (
              <button
                onClick={() => handleDeleteUnit(selectedUnit.id)}
                className="bg-red-600 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-red-700"
                data-testid="delete-unit-btn"
              >
                <Trash2 className="w-4 h-4 inline mr-2" />
                Delete
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => {
                  setEditDialogOpen(false);
                  setSelectedUnit(null);
                  setNewUnitPosition(null);
                }}
                className="btn-outline px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUnit}
                className="btn-primary px-4 py-2"
                data-testid="save-unit-btn"
              >
                <Save className="w-4 h-4 inline mr-2" />
                {selectedUnit ? 'Update' : 'Create'}
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
